import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  Paper,
  Tabs,
  Tab,
  useTheme,
  IconButton,
  InputAdornment,
  alpha,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonIcon from '@mui/icons-material/Person';

function TeamsForm() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { authAxios } = useAuth();
  
  const [title, setTitle] = useState('');
  const [name, setTeamName] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [formErrors, setFormErrors] = useState({
    title: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [imageFile, setImageFile] = useState(null);
    
  useEffect(() => {
    if (id) {
      const fetchTeamItem = async () => {
        setInitialLoading(true);
        try {
          const response = await authAxios.get(`/team/${id}`);
          const teamItem = response.data;
          setTitle(teamItem.title || '');
          setTeamName(teamItem.name || '');
          if (teamItem.image_url) {
            setPreviewImage(teamItem.image_url);
          }
        } catch (err) {
          console.error('Error fetching team item:', err);
          setError('Failed to load team item. Please try again.');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchTeamItem();
    }
  }, [id, authAxios]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle image drop
  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      console.log('File object:', file); // Log the file object to see available properties
      setImageFile(file);
      
      // Create a preview URL for the image
      setPreviewImage(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    maxFiles: 1
  });

  // Form validation
  const validateForm = () => {
    let isValid = true;
    const errors = {
      title: '',
      name: ''
    };
    
    const missing =[];

    if (!name.trim()) {
      errors.name = 'Name is required';
      missing.push('Name');
      isValid = false;
    }
    if (!title.trim()) {
      errors.title = 'Position is required';
      missing.push('Position');
      isValid = false;
    }
    if (!previewImage) {
      errors.image = 'Image is required';
      missing.push(' Team image');
      isValid = false;
    }

    setFormErrors(errors);
    if (!isValid) {
      setError(`Please fill in the following required fields: ${missing.join(', ')}`);
    }
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      let finalImageUrl = '';
      
      if (imageFile) {
        // Upload image to S3
        const formData = new FormData();
        formData.append('image', imageFile);
        
        const uploadResponse = await authAxios.post('/upload/image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        finalImageUrl = uploadResponse.data.imageUrl;
      } else if (previewImage && typeof previewImage === 'string' && !previewImage.startsWith('blob:')) {
        // If it's an existing URL from the database, keep it
        finalImageUrl = previewImage;
      }
      
      const TeamData = {
        title,
        name,
        image_url: finalImageUrl,
      };
      
      if (id) {
        // Update existing team item
        await authAxios.put(`/team/${id}`, TeamData);
      } else {
        // Create new team item
        await authAxios.post('/team', TeamData);
      }
      
      // Navigate back to team dashboard
      navigate('/team');
    } catch (err) {
      console.error('Error saving team item:', err);
      setError(
        err.response?.data?.message || 
        err.message || 
        'Failed to save team item. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Clear image
  const clearImage = () => {
    setPreviewImage(null);
    setImageFile(null);
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      noValidate
            sx={{ 
       width: '100%',              // full width of parent
        m: 0,
        px: { xs: 2, md: 3 },       // small padding, but still left-aligned
        py: { xs: 2, md: 4 },
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <IconButton 
          onClick={() => navigate('/team')}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" fontWeight="bold">
          {id ? 'Edit Team Item' : 'Create Team Item'}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 4 }} />
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <TextField
            required
            fullWidth
            id="name"
            label="name"
            name="name"
            autoFocus
            value={name}
            onChange={(e) => setTeamName(e.target.value)}
            disabled={loading}
            error={!!formErrors.name}
            helperText={formErrors.name}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          <TextField
            required
            fullWidth
            id="title"
            label="Position"
            name="title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
            error={!!formErrors.title}
            helperText={formErrors.title}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BadgeIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          

          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Image
            </Typography>
            
            <Paper
              {...getRootProps()}
              elevation={0}
              sx={{
                p: 3,
                border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 1,
                bgcolor: isDragActive ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                cursor: 'pointer',
                textAlign: 'center',
                mb: 2
              }}
            >
              <input {...getInputProps()} />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ImageIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                {isDragActive ? (
                  <Typography>Drop the image here...</Typography>
                ) : (
                  <Typography>Drag and drop an image here, or click to select</Typography>
                )}
              </Box>
            </Paper>
            
            {/* Image preview */}
            {previewImage && (
              <Box sx={{ mt: 2, position: 'relative' }}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1, 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={previewImage}
                    alt="Preview"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800x400?text=Invalid+Image';
                    }}
                    sx={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain'
                    }}
                  />
                </Paper>
                <IconButton
                  onClick={clearImage}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ width: '100%', mt: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button 
          onClick={() => navigate('/team')} 
          disabled={loading}
          variant="outlined"
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={<SaveIcon />}
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)'
          }}
        >
          {loading ? <CircularProgress size={24} /> : (id ? 'Save Changes' : 'Create Team')}
        </Button>
      </Box>
    </Box>
  );
}

export default TeamsForm;
